import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/**
 * 🍪 Cookie Policy Screen
 * - Public legal page for SafeVibe.dev
 * - Follows reference design: no card, no shadow, just centered content
 * - Uses Tailwind spacing, color, and font conventions
 * - No session or navigation logic
 * - Logs mount for debug/dev
 */
const CookiesScreen: React.FC = () => {
  useEffect(() => {
    // Minimal debug log for compliance
    console.log('Cookies screen loaded');
  }, []);

  return (
    <>
      <Navbar />
      <div className="max-w-2xl mx-auto py-12 px-4 text-gray-800" data-testid="cookies-content">
        {/* Page Title */}
        <h1 className="text-3xl font-bold mb-4">🍪 Cookie Policy – SafeVibe.dev</h1>
        {/* Meta Info */}
        <p className="mb-2 text-sm text-gray-500">Effective Date: 26.6.2025</p>
        <p className="mb-2 text-sm text-gray-500">Operator: SafeVibe.dev</p>
        <p className="mb-2 text-sm text-gray-500">Address: Beethovenstrasse 48, 8002 Zurich</p>
        <p className="mb-2 text-sm text-gray-500">Email: hello@safevibe.dev</p>
        <p className="mb-2 text-sm text-gray-500">Applies to: https://safevibe.dev and all subdomains</p>

        {/* Section 1: What Are Cookies */}
        <h2 className="text-xl font-semibold mt-8 mb-2">1. What Are Cookies?</h2>
        <p className="mb-4">Cookies are small text files stored on your device by your browser when you visit a website. They help us understand how visitors use the site, remember your preferences, and improve your experience.</p>

        {/* Section 2: What Cookies Do We Use */}
        <h2 className="text-xl font-semibold mt-8 mb-2">2. What Cookies Do We Use?</h2>
        <p className="mb-2 font-semibold">a) Essential Cookies</p>
        <table className="w-full text-left mb-4 border">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-3">Cookie</th>
              <th className="py-2 px-3">Purpose</th>
              <th className="py-2 px-3">Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="py-1 px-3">lang</td><td className="py-1 px-3">Language preference</td><td className="py-1 px-3">7 days</td></tr>
            <tr><td className="py-1 px-3">__stripe_*</td><td className="py-1 px-3">Enables secure payment via Stripe</td><td className="py-1 px-3">Up to 1 year</td></tr>
            <tr><td className="py-1 px-3">_supabase_auth_token</td><td className="py-1 px-3">Keeps you logged in via Supabase</td><td className="py-1 px-3">Session or max 7 days</td></tr>
          </tbody>
        </table>
        <p className="mb-2 font-semibold">b) Performance & Analytics Cookies</p>
        <p className="mb-4">We may use privacy-friendly analytics tools (e.g., Plausible, Fathom) that do not use tracking cookies. No third-party advertising or tracking cookies are used.</p>

        {/* Section 3: Cookie Consent */}
        <h2 className="text-xl font-semibold mt-8 mb-2">3. Cookie Consent</h2>
        <p className="mb-4">When you visit our site for the first time, you'll see a cookie banner. You can accept only essential cookies (default) or opt into analytics cookies (if offered in the future). Consent is stored for 30 days.</p>

        {/* Section 4: How to Delete or Block Cookies */}
        <h2 className="text-xl font-semibold mt-8 mb-2">4. How to Delete or Block Cookies</h2>
        <p className="mb-4">You can disable or delete cookies in your browser settings at any time. Note: Disabling essential cookies may affect site functionality.</p>

        {/* Section 5: Data Privacy */}
        <h2 className="text-xl font-semibold mt-8 mb-2">5. Data Privacy</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>We do not use cookies to track you across other websites</li>
          <li>We do not sell or share your data with third parties</li>
          <li>We do not store sensitive or personally identifiable information in cookies</li>
        </ul>
        <p className="mb-4">For full data usage details, please see our Privacy Policy.</p>

        {/* Section 6: Legal Basis */}
        <h2 className="text-xl font-semibold mt-8 mb-2">6. Legal Basis</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Swiss data protection law</li>
          <li>GDPR / DSGVO for EU-based users</li>
        </ul>
        <p className="mb-4">You always have the right to withdraw or update your consent.</p>

        {/* Section 7: Limitation of Liability */}
        <h2 className="text-xl font-semibold mt-8 mb-2">7. Limitation of Liability</h2>
        <p className="mb-4">SafeVibe.dev is provided "as is" and without any warranty. We do not guarantee that our service is error-free, secure, or will meet your needs. To the maximum extent permitted by law, we disclaim all liability for any direct, indirect, incidental, or consequential damages arising from your use of this website or our services.</p>

        {/* Section 8: Contact */}
        <h2 className="text-xl font-semibold mt-8 mb-2">8. Contact</h2>
        <p className="mb-2">Email: hello@safevibe.dev</p>
        <p className="mb-2">Address: Beethovenstrasse 48, 8002 Zurich</p>

        {/* Back to Home link for navigation */}
        <div className="flex justify-end mt-8">
          <Link to="/" className="text-blue-600 font-semibold hover:underline">Back to Home</Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CookiesScreen; 